import type { ApiResponse } from "@demore/shared/types";

export class ApiError extends Error {
  code: string;
  status: number;
  fields?: Record<string, string>;
  constructor(message: string, opts: { code: string; status: number; fields?: Record<string, string> }) {
    super(message);
    this.code = opts.code;
    this.status = opts.status;
    if (opts.fields) this.fields = opts.fields;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { keepalive?: boolean },
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    // keepalive lets the browser finish the request even if the page
    // navigates/closes — used for watch milestones so the final 100% isn't
    // lost when the player auto-advances to the done screen.
    keepalive: opts?.keepalive ?? false,
  });
  const text = await res.text();
  let payload: ApiResponse<T> | null = null;
  try {
    payload = text ? (JSON.parse(text) as ApiResponse<T>) : null;
  } catch {
    payload = null;
  }
  if (!res.ok || !payload || !payload.ok) {
    const err = payload && !payload.ok ? payload.error : null;
    throw new ApiError(err?.message || `Request failed (${res.status})`, {
      code: err?.code || "INTERNAL",
      status: res.status,
      fields: err?.fields,
    });
  }
  return payload.data;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown, opts?: { keepalive?: boolean }) =>
    request<T>("POST", path, body, opts),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
